import React from 'react';
import { X, ZoomIn } from 'lucide-react';

const TISSUE_COLORS = {
  Granulation: '#059669',
  Slough: '#D97706',
  Necrotic: '#DC2626',
  Epithelial: '#2563EB',
  Fibrin: '#7C3AED',
};

function getTissueColor(name) {
  for (const [k, v] of Object.entries(TISSUE_COLORS)) {
    if (name.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return '#64748B';
}

function InfoGrid({ children }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '12px 24px',
    }}>
      {children}
    </div>
  );
}

function InfoField({ label, children }) {
  return (
    <div>
      <div style={{
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: '0.9rem', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
        {children}
      </div>
    </div>
  );
}

export function ExaminationTable({ patientName, patientId, date, time, analysisId, imgUrl, onImageClick }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Examination Info</div>
      </div>
      <InfoGrid>
        <InfoField label="Patient Name">
          {patientName || <span style={{ color: 'var(--text-muted)' }}>N/A</span>}
        </InfoField>
        <InfoField label="Patient ID">
          {patientId
            ? <span className="badge badge-blue">{patientId}</span>
            : <span style={{ color: 'var(--text-muted)' }}>N/A</span>}
        </InfoField>
        <InfoField label="Date">
          {date || <span style={{ color: 'var(--text-muted)' }}>N/A</span>}
        </InfoField>
        <InfoField label="Time">
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {time || <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>N/A</span>}
          </span>
        </InfoField>
        <InfoField label="Analysis ID">
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
            {analysisId || <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>N/A</span>}
          </span>
        </InfoField>
        <InfoField label="Image">
          {imgUrl && imgUrl !== 'N/A'
            ? (
              <button
                className="btn btn-sm btn-outline"
                style={{ gap: 6, marginTop: 2 }}
                onClick={onImageClick}
              >
                <ZoomIn size={13} />
                View Image
              </button>
            )
            : <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>No image</span>
          }
        </InfoField>
      </InfoGrid>
    </div>
  );
}

export function AnalysisSummaryCard({ reportList }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Analysis Summary</div>
      </div>
      {reportList?.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {reportList.map((r, i) => {
            const type = r['Analysis Type'] || r.analysisType || 'N/A';
            const result = r['Result'] || r.result || 'N/A';
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '6px 16px',
                padding: '10px 14px',
                background: 'var(--surface)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-light)',
              }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {type}
                </span>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.82rem',
                  color: 'var(--blue-primary)',
                  background: 'rgba(27,108,168,0.08)',
                  padding: '2px 10px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                }}>
                  {result}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>
          No analysis data available.
        </p>
      )}
    </div>
  );
}

export function TissueCompositionCard({ woundTissue }) {
  const entries = woundTissue?.length && typeof woundTissue[0] === 'object'
    ? Object.entries(woundTissue[0])
    : [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Wound Tissue Composition</div>
      </div>
      {entries.length > 0 ? (
        <div>
          {entries.map(([key, value]) => {
            const pct = parseFloat(value) || 0;
            return (
              <div className="tissue-bar-wrap" key={key}>
                <div className="tissue-bar-label">
                  <span className="tissue-bar-name">{key}</span>
                  <span className="tissue-bar-pct">{pct.toFixed(1)}%</span>
                </div>
                <div className="tissue-bar-track">
                  <div
                    className="tissue-bar-fill"
                    style={{
                      width: `${Math.min(pct, 100)}%`,
                      background: getTissueColor(key),
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>
          No tissue data available.
        </p>
      )}
    </div>
  );
}

export function WoundStageCard({ woundStageData }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Wound Stage Classification</div>
      </div>
      {woundStageData?.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {woundStageData.map((item, i) => {
            const conf = item.Confidence != null ? Number(item.Confidence) : null;
            const highConf = conf !== null && conf >= 70;
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px 16px',
                padding: '12px 14px',
                background: 'var(--surface)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border-light)',
              }}>
                <span style={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 80 }}>
                  {item.Stage || 'N/A'}
                </span>
                {conf !== null && (
                  <>
                    <span style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.82rem',
                      color: highConf ? 'var(--success)' : 'var(--warning)',
                      minWidth: 52,
                    }}>
                      {conf.toFixed(2)}%
                    </span>
                    <div style={{
                      flex: 1,
                      minWidth: 80,
                      height: 8,
                      background: 'var(--border-light)',
                      borderRadius: 4,
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.min(conf, 100)}%`,
                        background: highConf ? 'var(--success)' : 'var(--warning)',
                        borderRadius: 4,
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.9rem' }}>
          No stage data available.
        </p>
      )}
    </div>
  );
}

export function AdviceCard({ adviceList }) {
  if (!adviceList?.length) return null;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div className="card-title">Care Advice</div>
      </div>
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {adviceList.map((text, i) => (
          <li key={i} style={{
            display: 'flex',
            gap: 10,
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
            padding: '8px 12px',
            background: 'var(--success-bg)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid rgba(5,150,105,0.15)',
          }}>
            <span style={{ color: 'var(--teal)', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            {text}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ImageModal({ imgUrl, onClose }) {
  const headerHeight = parseInt(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--header-height') || '64'
  );

  React.useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10,22,40,0.85)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingTop: headerHeight + 60,
        paddingLeft: 16,
        paddingRight: 16,
        paddingBottom: 16,
        boxSizing: 'border-box',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: 800,
          margin: '0 auto',
          background: 'var(--navy)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-xl)',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close image"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'rgba(255,255,255,0.15)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            zIndex: 10,
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
        >
          <X size={18} />
        </button>

       <img
  src={imgUrl}
  alt="Wound"
  style={{
    display: 'block',
    width: '100%',
    maxHeight: '70vh',
    objectFit: 'contain',
  }}
/>
      </div>
    </div>
  );
}